<template>
  <div :class="['form-element', hidden && 'd-none']">
    <FormLabel
      v-if="label"
      v-bind="{
        label,
        required,
        disabled,
        description,
        descriptionType,
        dirty,
        error,
        id
      }"
    />
    <div
      :class="['position-relative', 'rounded-5 pt-3', !disabledOrReadOnly && 'border', transparencyBg && 'transparency-bg']"
      style="min-height: 64px"
    >
      <div class="row pl-3">
        <p v-if="disabledOrReadOnly && !valueNormalized.length">—</p>
        <AppFullscreenPreview
          v-if="isFullscreenPreview"
          @close="setFullscreenPreview"
        >
          <CommonSwiper
            v-if="valueNormalized.length > 1"
            :swiperSlides="valueNormalized"
            :options="swiperOptions"
            :swiperStyle="{ width: 'calc(100vw - 400px)' }"
          >
            <template #slide="{ slide: imageUrl }">
              <img
                :src="imageUrl"
                class="img-thumbnail img-fluid"
                alt=""
              />
            </template>
          </CommonSwiper>
          <img
            v-else
            :src="valueNormalized[0]"
          >
        </AppFullscreenPreview>
        <div
          v-if="isBusy"
          class="form-upload-image__spinner"
        >
          <AppSpinner variant="primary" />
        </div>
        <div
          v-for="imageUrl of valueNormalized"
          :key="imageUrl"
          :class="['col pl-0', `col-${columnsCount}`, multiple && 'mb-3']"
        >
          <div
            :style="{ backgroundImage: `url(${imageUrl})` }"
            class="form-upload-image__image rounded-5"
            data-test="formUploadImagePreview"
          >
            <div
              v-if="!(disabledOrReadOnly || isBusy)"
              class="form-upload-image__remove"
            >
              <AppTooltip placement="bottom">
                <AppButton class="form-upload-image__remove-icon mr-3">
                  <AppIcon
                    name="delete_forever"
                    class="text-danger"
                    :size="40"
                    data-test="removeImage"
                    @click="removeImage(imageUrl)"
                  />
                </AppButton>
                <template #tooltip>
                  <span>Remove Image</span>
                </template>
              </AppTooltip>
              <AppTooltip placement="bottom">
                <AppButton class="form-upload-image__remove-icon mr-3">
                  <AppIcon
                    name="fullscreen"
                    class="text-dark"
                    :size="40"
                    @click="setFullscreenPreview(true)"
                  />
                </AppButton>
                <template #tooltip>
                  <span>Show in full screen</span>
                </template>
              </AppTooltip>
              <AppTooltip placement="bottom">
                <AppButton
                  class="form-upload-image__remove-icon"
                  data-test="formUploadImageCrop"
                >
                  <AppIcon
                    name="crop"
                    class="text-dark"
                    :size="40"
                    @click="showEditorModal = true"
                  />
                </AppButton>
                <template #tooltip>
                  <span>Crop</span>
                </template>
              </AppTooltip>
            </div>
          </div>
        </div>
        <div
          v-if="!disabledOrReadOnly"
          :class="[
            'col mb-3 pl-0 d-flex justify-content-center align-items-center',
            valueNormalized.length ? 'col-2' : 'col-12',
          ]"
        >
          <button
            v-if="!isBusy && (multiple || !value || !valueNormalized.length)"
            type="button"
            class="d-flex align-items-center justify-content-center flex-column p-0 border-0 cursor-pointer form-upload-button"
            data-test="openImageSelectButton"
            @click="openImageSelect"
          >
            <AppIcon
              name="add_photo_alternate"
              class="text-primary"
              :size="40"
            />
            <span class="text-primary text-uppercase font-weight-medium mt-1">{{ uploadText }}</span>
          </button>
        </div>
      </div>
      <input
        :id="id"
        ref="fileInput"
        type="file"
        accept="image/*"
        class="d-none"
        :multiple="multiple"
        @change="loadImages"
      />
    </div>
    <div v-if="supportAltText">
      <FormText
        :value="altValue"
        v-bind="{
          disabled,
        }"
        data-test="image-alt-text"
        label="Image description"
        placeholder="Example: Group of students sitting in a math class"
        @input="setAlt"
      />
      <p class="small mt-2 mb-0">
        <strong>Important:</strong>
        The Web Content Accessibility Guidelines require all images to have a description to convey the
        purpose of the image to users using assistive technologies.
        Please provide a description to ensure your catalog remains WCAG 2.0 compliant.
      </p>
    </div>
    <AppModal
      v-if="showEditorModal"
      :showX="false"
      @close="closeEditor"
    >
      <template #modal-header>
        <AppHeading>Crop Image</AppHeading>
      </template>
      <template #modal-body>
        <CommonImageCrop
          :value="files[0] || valueNormalized[0]"
          style="min-height: 400px"
          @input="image => editedImage = image"
        />
      </template>
      <template #modal-footer>
        <AppButton @click="closeEditor">Cancel</AppButton>
        <div class="mx-auto"></div>
        <AppButton
          :raised="true"
          @click="uploadCroppedImage"
        >
          Upload
        </AppButton>
      </template>
    </AppModal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import CommonImageCrop from '@/components/Common/CommonImageCrop'
import CommonSwiper from '@/components/Common/CommonSwiper'
import FormMixin from './FormMixin'
import FormLabel from './FormLabel'
import FormText from './FormText'
import { uploadToS3 } from '@/api'
import _isObject from 'lodash/isObject'

export default {
  mixins: [ FormMixin ],
  components: { CommonSwiper, CommonImageCrop, FormLabel, FormText },
  props: {
    value: {
      type: [ String, Array, Object ],
      default: null,
      validator (value) {
        if (Array.isArray(value)) return value.every(v => typeof v === 'string')
        return true
      },
    },
    uploadProvider: {
      type: Function,
      default: null,
    },
    useDefaultProvider: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    transparencyBg: {
      type: Boolean,
      default: true,
    },
    editor: {
      type: Boolean,
      default: false,
    },
    validators: {
      type: Array,
      default: null,
    },
    supportAltText: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    files: [],
    images: [],
    editedImage: undefined,
    isUploading: false,
    isFetching: false,
    isFullscreenPreview: false,
    showEditorModal: false,
    // Required to refresh image after cropping, since cropped image has the same url as original
    timestamp: Date.now(),
  }),
  computed: {
    ...mapGetters([ 'school' ]),
    uploadProviderComputed () {
      return this.useDefaultProvider ? this.defaultUploadProvider : this.uploadProvider
    },
    disabledOrReadOnly () {
      return this.disabled || this.readOnly
    },
    isValueObject () {
      return _isObject(this.value)
    },
    isValueArray () {
      return Array.isArray(this.urlValue)
    },
    isValueString () {
      return typeof this.urlValue === 'string'
    },
    urlValue () {
      return this.isValueObject ? this.value.url : this.value
    },
    altValue () {
      return this.isValueObject ? this.value.altText : null
    },
    valueNormalized () {
      if (this.isValueArray) return this.urlValue.map(img => `${img}?now=${this.timestamp}`)
      if (this.isValueString) return [ `${this.urlValue}?now=${this.timestamp}` ]
      return []
    },
    uploadText () {
      if (this.multiple) return this.urlValue && this.urlValue.length ? 'Add more images' : 'Add images'
      return 'Add image'
    },
    columnsCount () {
      if (this.multiple) {
        switch (this.valueNormalized.length) {
          case 1: return 10
          case 2: return 5
          case 3: return 4
          default: return 3
        }
      }
      return 12
    },
    swiperOptions () {
      return {
        allowSlidePrev: this.valueNormalized.length > 1,
        allowSlideNext: this.valueNormalized.length > 1,
      }
    },
    isBusy () {
      return this.isUploading || this.isFetching
    },
  },
  watch: {
    valueNormalized: {
      immediate: true,
      deep: true,
      async handler (urls) {
        // It allows to detect if all images has been fetched and loaded
        try {
          this.isFetching = true
          await Promise.all(urls.map(url => new Promise((resolve, reject) => {
            const img = new Image()
            img.onload = resolve
            img.onerror = reject
            img.src = url
            if (img.complete) img.onload()
          })))
          this.isFetching = false
        } catch (err) {
          this.isFetching = false
          throw err
        }
      },
    },
  },
  beforeDestroy () {
    // Clear the input files
    if (Array.isArray(this.images) && !this.uploadProviderComputed) this.garbageRuntimeImages(this.images)
    this.images = undefined
  },
  mounted () {
    if (this.editor && this.multiple) {
      // It's hard from UI/UX perspective to allow to edit multiple files. It'd require some extra components
      throw new Error('Photo editor can be used only in single mode')
    }
  },
  methods: {
    async defaultUploadProvider (image) {
      try {
        const imageUrl = await uploadToS3({
          school: this.school,
          file: image,
          bucketName: 'coursedog-images-public',
        })
        return imageUrl.split('?')[0]
      } catch (err) {
        alert('Something went wrong, please try another file')
        console.error(err)
        return null
      }
    },
    openImageSelect () {
      this.$refs.fileInput.click()
    },
    getImagesMerged (images) {
      return this.isValueArray ? [ ...this.urlValue, ...images ] : images
    },
    async loadImages () {
      // Has to spread, as FileList is not a proper Array.
      this.files = [ ...this.$refs.fileInput.files ]
      this.images = this.files.map(URL.createObjectURL)
      if (this.editor) {
        this.showEditorModal = true
      } else {
        this.uploadImages(this.files)
      }
    },
    async validate (images) {
      if (this.validators && this.validators.length) {
        const imgArray = images === null ? []
          : Array.isArray(images) ? images : [ images ]
        const validations = await Promise.all(imgArray.map(i => Promise.all(
          this.validators.map(async v => v(i)
          ))))
        this.$emit('validated', validations)
      }
    },
    async emitImages (images) {
      await this.validate(images)
      if (this.supportAltText) {
        this.$emit('input', {
          url: images,
          altText: this.altValue,
        })
      } else {
        this.$emit('input', images)
      }
    },
    async uploadImages (images) {
      try {
        this.isUploading = true
        const urls = this.uploadProviderComputed
          ? await Promise.all(images.map(this.uploadProviderComputed))
          : this.images
        this.emitImages(this.multiple ? this.getImagesMerged(urls) : urls[0])
        this.timestamp = Date.now()
      } catch (err) {
        console.error(err)
      } finally {
        this.$refs.fileInput.value = null
        this.isUploading = false
      }
    },
    closeEditor () {
      this.garbageRuntimeImages(this.images)
      this.showEditorModal = false
      this.$refs.fileInput.value = null
      this.isUploading = false
    },
    async uploadCroppedImage () {
      this.showEditorModal = false
      await this.uploadImages([ this.editedImage ])
      this.closeEditor()
    },
    removeImage (imageUrl) {
      // TODO: Should probably have some endpoint / use provider to remove the image of s3 as well.
      this.emitImages(this.multiple ? this.urlValue.filter(image => image !== imageUrl) : null)
    },
    garbageRuntimeImages (images) {
      images.forEach(URL.revokeObjectURL)
    },
    setFullscreenPreview (isPreviewing) {
      this.isFullscreenPreview = isPreviewing
    },
    setAlt ($ev) {
      if (this.supportAltText) {
        this.$emit('input', {
          url: this.urlValue,
          altText: $ev,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/css/mixins/transition";

.form-upload-image__image {
  height: 200px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
}

.form-upload-image__remove {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  opacity: 0;
  @include transition(opacity);

  &::after {
    position: absolute;
    display: block;
    top: 0;
    left: 0;
    z-index: 5;
    width: 100%;
    height: 100%;
    background-color: transparent;
    content: "";
    @include transition(background-color);
  }
}

.form-upload-image__remove-icon {
  position: relative;
  z-index: 10;
  padding: 7px;
  background: #fff;
}

.form-upload-image__image:hover {
  .form-upload-image__remove {
    opacity: 1;

    &::after {
      background-color: rgba(0, 0, 0, 0.4);
    }
  }
}

.form-upload-button {
  background: none;

  &:hover .text-primary {
    color: #0a5199 !important;
  }
}

.transparency-bg {
  background-image: linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
    linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
    linear-gradient(-45deg, transparent 75%, #e0e0e0 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
}

.form-upload-image__spinner {
  position: absolute;
  z-index: 1;
  left: 50%;
  top: 50%;
  margin-left: -32px;
  margin-top: -32px;
  padding: 16px;
}
</style>
